import { BestOfRingVideo } from '@amzn/ring-neighbors-contentful-gql-schema';
import { Grid, GridItem, GridItemProps } from '@amzn/ring-ui-react-components';
import { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import poster from 'src/assets/images/transparent-background-mini.png';
import { useNeighborsWebClickEventStreamDispatcher } from 'src/components/App/hooks';
import { VideoPlayer } from 'src/features/BestOfRing/components/Media/VideoPlayer';
import { useIsMobile, useOnScreen } from 'src/shared/hooks';
import { Thumbnail } from '../Media/Thumbnail';

export interface VideoGridProps extends GridItemProps {
  videos: BestOfRingVideo[];
}

export interface VideoGridItemProps extends GridItemProps {
  video: BestOfRingVideo;
}

export const VideoGrid = ({ videos }: VideoGridProps) => {
  const history = useHistory();

  const dispatchClick =
    useNeighborsWebClickEventStreamDispatcher('nhWeb_bestOfRing');

  const handleClick = useCallback(
    (v: BestOfRingVideo) => {
      if (dispatchClick) {
        dispatchClick('nhWeb_videoClick', [v.name ?? ''], {
          itemType: 'button',
        });
      }
      history.push('/best-of-ring/' + v.sys.id);
    },
    [history, dispatchClick],
  );

  const listItems = useMemo(() => {
    const items = [];

    for (const v of videos) {
      if (!v) continue;

      items.push(
        v.orientation === 'Portrait' ? (
          <PortraitVideoItemGrid
            onClick={() => handleClick(v)}
            video={v}
            key={v?.sys?.id}
          />
        ) : (
          <LandscapeVideoItemGrid
            onClick={() => handleClick(v)}
            video={v}
            key={v?.sys?.id}
          />
        ),
      );
    }

    return items;
  }, [videos, handleClick]);

  return (
    <Grid gap="8px" w="100%" data-testid="video-grid">
      {listItems}
    </Grid>
  );
};

const PortraitVideoItemGrid = ({ video, ...props }: VideoGridItemProps) => {
  const isMobile = useIsMobile();
  const thumbnail: string = isMobile
    ? (video.mobileThumbnail?.url as string)
    : (video.thumbnail?.url as string);
  return (
    <GridItem
      cursor="pointer"
      bgColor="black"
      colSpan={2}
      borderRadius="8px"
      overflow="hidden"
      data-testid="portrait-video-grid-item"
      {...props}
    >
      <Thumbnail
        src={thumbnail}
        orientation="portrait"
        caption={video.title || undefined}
      />
    </GridItem>
  );
};

const LandscapeVideoItemGrid = ({ video, ...props }: VideoGridItemProps) => {
  const [playing, setPlaying] = useState(false);
  const isMobile = useIsMobile();
  const thumbnail: string = isMobile
    ? (video.mobileThumbnail?.url as string)
    : (video.thumbnail?.url as string);
  const ref = useOnScreen(([entry]) => setPlaying(entry.isIntersecting), 0.1);
  return (
    <GridItem
      cursor="pointer"
      bgColor="black"
      colSpan={4}
      borderRadius="8px"
      overflow="hidden"
      ref={ref}
      data-testid="landscape-video-grid-item"
      {...props}
    >
      {video.optimizedVideo ? (
        <VideoPlayer
          poster={poster}
          playing={playing}
          loop={true}
          muted={true}
          url={video.optimizedVideo.url!}
          orientation="landscape"
          caption={video.title || undefined}
        />
      ) : (
        <Thumbnail
          src={thumbnail}
          orientation="landscape"
          caption={video.title || undefined}
        />
      )}
    </GridItem>
  );
};
